import React from 'react';
import { Drawer, Table, Spin, Button, Popconfirm } from 'antd';
import { Trash2, Store, User, Clock, Package, Image as ImageIcon } from 'lucide-react';
import { fmtVZ } from '../../../utils/timeUtils';
import { EmptyState } from '../../common';
import AuditImageThumb from './AuditImageThumb';
import AuditStatusBadge from './AuditStatusBadge';
import AuditFlagChips, { BarcodeIcon } from './AuditFlagChips';
import AuditPriceDisplay from './AuditPriceDisplay';
import { useStoreCheckRecords } from './auditDrawers.controller';

export default function AuditStoreCheckDrawer({ open, onClose, storeCheckId, onFetchRecords, onRecordClick, onDelete, deleting }) {
    const { loading, storeCheck, records } = useStoreCheckRecords({ open, storeCheckId, onFetchRecords });

    const columns = [
        {
            title: 'Imagen',
            width: 72,
            render: (_, r) => <AuditImageThumb record={r} onClick={() => onRecordClick?.(r)} />,
        },
        {
            title: 'Código',
            dataIndex: 'code',
            render: (code, r) => (
                <div className="flex items-center gap-2">
                    <BarcodeIcon barcodeMeta={r.barcodeMeta} hasCatalogMatch={!!r.auditProduct} />
                    <span className="tabular-nums">{code}</span>
                </div>
            ),
        },
        {
            title: 'Precio (USD)',
            width: 120,
            align: 'right',
            render: (_, r) => (
                <AuditPriceDisplay
                    price={r.price}
                    currency={r.currency}
                    priceUsd={r.priceUsd}
                    priceConversion={r.priceConversion}
                    isPromo={r.isPromo}
                    promoPrice={r.promoPrice}
                    promoPriceUsd={r.promoPriceUsd}
                    promoPriceConversion={r.promoPriceConversion}
                />
            ),
        },
        {
            title: 'Estado',
            width: 120,
            render: (_, r) => <AuditStatusBadge type="image" status={r.imageProcessingStatus} />,
        },
        {
            title: 'Flags',
            render: (_, r) => <AuditFlagChips flags={r.flags} barcodeMeta={r.barcodeMeta} showBarcode={false} />,
        },
    ];

    return (
        <Drawer
            title={storeCheck ? `Visita — ${storeCheck.storeId?.name || 'Tienda'}` : 'Detalle de visita'}
            width={800}
            open={open}
            onClose={onClose}
            destroyOnClose
            extra={
                storeCheck &&
                onDelete && (
                    <Popconfirm
                        title="¿Eliminar visita y todos sus escaneos?"
                        description="Esta acción no se puede deshacer."
                        onConfirm={() => onDelete?.(storeCheck)}
                        okText="Sí, eliminar"
                        okType="danger"
                        cancelText="Cancelar"
                        getPopupContainer={() => document.body}>
                        <Button danger icon={<Trash2 />} loading={deleting} size="small">
                            Eliminar visita
                        </Button>
                    </Popconfirm>
                )
            }>
            {loading ? (
                <div className="flex justify-center py-12">
                    <Spin />
                </div>
            ) : !storeCheck ? (
                <EmptyState icon={<Store />} title="Sin datos de la visita" />
            ) : (
                <>
                    <div className="mb-4 rounded-xl border border-surface-200 bg-surface-50 p-4">
                        <div className="flex items-center gap-2 text-[15px] font-semibold text-surface-900">
                            <Store className="text-surface-400" />
                            {storeCheck.storeId?.name}
                        </div>
                        <div className="mt-2 flex items-center gap-1.5 text-sm text-surface-600">
                            <User className="text-surface-400" />
                            Auditor: {storeCheck.userId?.name} ({storeCheck.userId?.email})
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-surface-500 tabular-nums">
                            <span className="inline-flex items-center gap-1.5">
                                <Clock className="text-surface-400" />
                                {fmtVZ(storeCheck.startTime)} → {fmtVZ(storeCheck.endTime)}
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                                <Package className="text-surface-400" />
                                {storeCheck.audited ?? records.length} productos
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                                <ImageIcon className="text-surface-400" />
                                {storeCheck.totalImages ?? 0} imágenes
                            </span>
                        </div>
                    </div>
                    <div className="overflow-hidden rounded-xl border border-surface-200">
                        <Table
                            rowKey="_id"
                            size="small"
                            dataSource={records}
                            columns={columns}
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            rowClassName={() => 'cursor-pointer'}
                            locale={{ emptyText: <EmptyState icon={<Package />} title="Sin escaneos en esta visita" /> }}
                            onRow={(r) => ({ onClick: () => onRecordClick?.(r) })}
                        />
                    </div>
                </>
            )}
        </Drawer>
    );
}
